import { useCallback, useEffect, useMemo, useState } from 'react'
import { Avatar } from '@/components/ui/Avatar'
import { getClient } from '@/lib/matrix/client'
import { useReceiptStore, selectReceiptVersion } from '@/lib/store/receipt-store'
import { formatDate, formatTime } from '@/lib/utils/time'
import type { SerializedEvent } from '@/lib/matrix/events'

interface MessageInfoModalProps {
  event: SerializedEvent
  roomId: string
  onClose: () => void
}

interface ReaderRow {
  userId: string
  name: string
  mxc: string | null
  readTs: number
}

function bodyPreview(event: SerializedEvent): string {
  const msgtype = event.content.msgtype as string | undefined
  if (event.type === 'm.sticker') return '🩷 Sticker'
  switch (msgtype) {
    case 'm.image':
      return '📷 Photo'
    case 'm.video':
      return '🎬 Video'
    case 'm.audio':
      return '🎵 Audio'
    case 'm.file':
      return '📎 File'
    case 'm.location':
      return '📍 Location'
    default:
      return (event.content.body as string) || '(no text)'
  }
}

function whenLabel(ts: number): string {
  const now = new Date()
  const sameDay = new Date(ts).toDateString() === now.toDateString()
  return sameDay ? formatTime(ts) : `${formatDate(ts)} · ${formatTime(ts)}`
}

export function MessageInfoModal({ event, roomId, onClose }: MessageInfoModalProps) {
  const receiptVersion = useReceiptStore(selectReceiptVersion(roomId))
  const [copied, setCopied] = useState(false)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const { readers, otherMemberCount } = useMemo(() => {
    const client = getClient()
    const room = client?.getRoom(roomId)
    if (!client || !room) return { readers: [] as ReaderRow[], otherMemberCount: 0 }
    const me = client.getUserId()

    const rows: ReaderRow[] = []
    let others = 0
    for (const member of room.getJoinedMembers()) {
      // The sender trivially "read" their own message; skip them and ourselves.
      if (member.userId === event.sender) continue
      others += 1
      if (member.userId === me) continue
      const rr = room.getReadReceiptForUserId(member.userId)
      const ts = rr?.data?.ts
      if (typeof ts === 'number' && ts >= event.timestamp) {
        rows.push({
          userId: member.userId,
          name: member.name || member.userId,
          mxc: member.getMxcAvatarUrl() ?? null,
          readTs: ts,
        })
      }
    }
    rows.sort((a, b) => b.readTs - a.readTs)
    return { readers: rows, otherMemberCount: others }
    // receiptVersion is the recompute trigger; intentionally in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, event.eventId, event.sender, event.timestamp, receiptVersion])

  const client = getClient()
  const sender = client?.getRoom(roomId)?.getMember(event.sender)
  const senderName = sender?.name || event.senderName || event.sender
  const isEdited = !!event.content['m.new_content']

  const copyId = () => {
    navigator.clipboard.writeText(event.eventId).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] flex flex-col bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--np-border))]">
          <h3 className="text-sm font-semibold text-[rgb(var(--np-tx-primary))]">Message info</h3>
          <button
            onClick={onClose}
            className="p-1 rounded text-[rgb(var(--np-tx-muted))] hover:text-[rgb(var(--np-tx-primary))] hover:bg-[rgb(var(--np-raised))] transition-colors"
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-4 py-3">
          {/* Message preview */}
          <div className="flex items-start gap-3 bg-[rgb(var(--np-raised))] rounded-md p-3">
            <Avatar mxc={sender?.getMxcAvatarUrl() ?? event.senderAvatar} name={senderName} userId={event.sender} size={32} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[rgb(var(--np-tx-primary))]">{senderName}</p>
              <p className="text-sm text-[rgb(var(--np-tx-secondary))] break-words line-clamp-4 whitespace-pre-wrap">
                {bodyPreview(event)}
              </p>
            </div>
          </div>

          {/* Metadata */}
          <dl className="mt-3 space-y-2 text-xs">
            <Row label="Sent">{whenLabel(event.timestamp)}</Row>
            <Row label="Type">{(event.content.msgtype as string) || event.type}</Row>
            {isEdited && <Row label="Edited">yes</Row>}
            <Row label="Event ID">
              <button
                onClick={copyId}
                className="font-data text-[rgb(var(--np-tx-secondary))] hover:text-[rgb(var(--np-accent))] transition-colors break-all text-left"
                title="Copy event ID"
              >
                {copied ? 'Copied!' : event.eventId}
              </button>
            </Row>
          </dl>

          {/* Read by */}
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[rgb(var(--np-tx-muted))] mb-2">
              Read by {readers.length}{otherMemberCount > 0 ? ` / ${otherMemberCount}` : ''}
            </p>
            {readers.length === 0 ? (
              <p className="text-xs text-[rgb(var(--np-tx-muted))] italic">No read receipts yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {readers.map((r) => (
                  <li key={r.userId} className="flex items-center gap-2.5">
                    <Avatar mxc={r.mxc} name={r.name} userId={r.userId} size={28} />
                    <span className="flex-1 min-w-0 text-sm text-[rgb(var(--np-tx-primary))] truncate">{r.name}</span>
                    <span className="text-[10px] font-data text-[rgb(var(--np-tx-muted))]" title={new Date(r.readTs).toLocaleString()}>
                      {whenLabel(r.readTs)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-20 shrink-0 text-[rgb(var(--np-tx-muted))]">{label}</dt>
      <dd className="flex-1 min-w-0 text-[rgb(var(--np-tx-secondary))]">{children}</dd>
    </div>
  )
}
